/* eslint-disable no-restricted-imports */
import React, {useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton/index.ts'
import {
    UIGameResourceCardsTooltipContent,
    type UIGameResourceCardsTooltipContentHandle,
} from '../../../common/UIGameResourceCardsTooltipContent/index.ts'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PieceTypes} from '@colonist/game-base-types'
import {ACTION_BUTTON_IDS, useActionButtonCommonErrors} from '../utils.ts'
import {CITY_IMAGE_PATH} from './utils.ts'
import {useGameStore} from '../../../store/GameStoreContext.ts'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators.ts'
import {ActionState} from '@colonist/game-states'
import {useMechanicValidatorCity} from '../../../store/selectors/useMechanicValidatorCity.ts'
import {type TranslationObject} from '@colonist/i18n'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import clsx from 'clsx'
import styles from '../UIGameActionButtonContainer.module.scss'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext.tsx'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator.ts'

const cardsToBuildCity = getCardsForPiece(PieceTypes.City)

export const UIGameActionButtonCity = ({
    className,
}: {
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const {getPlayerValidator} = usePlayerValidators()
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const playerValidator = getPlayerValidator(myColor)
    const {sendBuildCity, sendCancelAction, sendClearCornerHighlight} = useUIGameSocketContext()
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const currentStateValidator = useCurrentStateValidator()
    const cityValidator = useMechanicValidatorCity()

    const actionStatus = useMemo(() => {
        if(!cityValidator) {
            return {canTakeAction: false}
        }
        const canPlaceCityActionStatus = cityValidator.canPlayerPlaceCity(myColor)

        return canPlaceCityActionStatus
    }, [cityValidator, myColor])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToBuildCity)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const icon = CITY_IMAGE_PATH[myColor]
    const isHidden = beginnerActionState?.hiddenUIElements.includes(HiddenUIElement.CityActionButton)
    const disabled = hasCommonErrors || !actionStatus.canTakeAction || isHidden
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isPlacingCity = actionState === ActionState.PlaceCity && !disabled
    const bankCityAmount = cityValidator?.getCityPlayerState(myColor).bankCityAmount ?? 0
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction

    const onClick = () => {
        if(disabled) return
        if(isPlacingCity) {
            sendClearCornerHighlight()
            sendCancelAction()
        } else {
            sendBuildCity()
        }
    }

    return <UIGameActionButton
        className={clsx(className, {
            [styles.hidden]: isHidden,
        })}
        id={ACTION_BUTTON_IDS.buildCity}
        count={bankCityAmount}
        countColor={myColor}
        disabled={disabled}
        helperText={helperText}
        highlight={isPlacingCity}
        iconPath={icon}
        onClickActionIfDisabled={triggerFlickerCardsAnimation}
        tooltipCustomMenuComponent={
            <UIGameResourceCardsTooltipContent
                cardInventory={playerValidator.resourceCards.cards}
                cardsToBuild={cardsToBuildCity}
                ref={tooltipRef}/>
        }
        onClick={onClick}
    />
}
